import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ✅ Feed update item
export interface FeedUpdate {
  id: string;
  message: string;
  timestamp: number;
}

// ✅ Simplified progress interface - server calculates, frontend displays
export interface SimpleLegalProgress {
  queryId: string;
  chatId: string;
  caseId: string;
  overallProgress: number; // 0-100, calculated by backend
  currentStage: string;
  stageDetails: string;
  status: 'active' | 'completed' | 'error';
  startTime: number;
  lastUpdate: number;
  feedUpdates: FeedUpdate[]; // Activity feed (non-progress-advancing updates)
  renderedFeedIds: string[]; // Track which feed updates have been added (prevent duplicates during polling)
}

export interface SimpleLegalProgressState {
  activeProgresses: Record<string, SimpleLegalProgress>; // queryId -> progress
  pollingChats: Record<string, { intervalId: number; messageId: string; lastChecked: number }>; // chatId -> polling info
}

const initialState: SimpleLegalProgressState = {
  activeProgresses: {},
  pollingChats: {},
};

const simpleLegalProgressSlice = createSlice({
  name: 'simpleLegalProgress',
  initialState,
  reducers: {
    // ✅ Start new progress (called when query starts)
    startProgress: (state, action: PayloadAction<{ 
      queryId: string; 
      chatId: string; 
      caseId: string; 
    }>) => {
      const { queryId, chatId, caseId } = action.payload;
      
      state.activeProgresses[queryId] = {
        queryId,
        chatId,
        caseId,
        overallProgress: 0,
        currentStage: 'initialization',
        stageDetails: 'Starting legal analysis...',
        status: 'active',
        startTime: Date.now(),
        lastUpdate: Date.now(),
        feedUpdates: [],
        renderedFeedIds: []
      };
      
      console.log(`✅ [SIMPLE_PROGRESS] Started progress for queryId: ${queryId}, chatId: ${chatId}`);
    },

    // ✅ Update progress from MQTT (server-calculated values)
    updateProgress: (state, action: PayloadAction<{
      queryId: string;
      overallProgress: number;
      currentStage: string;
      stageDetails: string;
      status: 'active' | 'completed' | 'error';
      chatId?: string;
      caseId?: string;
      advancesProgress?: boolean; // true = milestone, false = feed update
      feedId?: string; // Optional: ID from DB for deduplication
      feedTimestamp?: number; // Optional: timestamp from DB
    }>) => {
      const { queryId, overallProgress, currentStage, stageDetails, status, chatId, caseId, advancesProgress = true, feedId, feedTimestamp } = action.payload;
      let progress = state.activeProgresses[queryId];
      
      if (!progress) {
        // ✅ Auto-create progress object for unknown queryIds (e.g., after refresh)
        console.warn(`⚠️ [SIMPLE_PROGRESS] Auto-creating progress for unknown queryId: ${queryId}`);
        progress = {
          queryId,
          chatId: chatId || 'unknown',
          caseId: caseId || 'unknown',
          overallProgress: 0,
          currentStage: 'initialization',
          stageDetails: 'Restoring progress...',
          status: 'active',
          startTime: Date.now(),
          lastUpdate: Date.now(),
          feedUpdates: [],
          renderedFeedIds: []
        };
        state.activeProgresses[queryId] = progress;
      }
      
      if (advancesProgress) {
        // ✅ Milestone update - update progress bar
        progress.overallProgress = overallProgress;
        progress.currentStage = currentStage;
        progress.stageDetails = stageDetails;
        progress.status = status;
        progress.lastUpdate = Date.now();
        console.log(`🔄 [SIMPLE_PROGRESS] Milestone update - queryId: ${queryId}, progress: ${overallProgress}%, stage: ${currentStage}`);
      } else {
        // ✅ Feed update - add to feed (keep last 50 items)
        // Use feedId from DB if available, otherwise generate a new one
        const finalFeedId = feedId || `feed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const finalTimestamp = feedTimestamp || Date.now();
        
        // Check if this feed ID has already been rendered
        const isAlreadyRendered = progress.renderedFeedIds.includes(finalFeedId);
        
        if (!isAlreadyRendered) {
          const feedItem: FeedUpdate = {
            id: finalFeedId,
            message: stageDetails,
            timestamp: finalTimestamp
          };
          progress.feedUpdates.push(feedItem);
          progress.renderedFeedIds.push(finalFeedId);
          
          console.log(`📢 [SIMPLE_PROGRESS] Feed update added!`, {
            queryId,
            message: stageDetails.substring(0, 60),
            feedId: feedItem.id,
            totalFeedItems: progress.feedUpdates.length,
            fromDB: !!feedId
          });
          
          // Keep only last 50 feed items to prevent memory issues
          if (progress.feedUpdates.length > 50) {
            progress.feedUpdates = progress.feedUpdates.slice(-50);
            progress.renderedFeedIds = progress.renderedFeedIds.slice(-50);
          }
        } else {
          console.log(`⏭️ [SIMPLE_PROGRESS] Skipping duplicate feed update (ID already rendered): ${stageDetails.substring(0, 40)}...`);
        }
        
        progress.lastUpdate = Date.now();
      }
      
      // ✅ MQTT priority: Mark that MQTT is working (don't actually stop polling - let the hook manage that)
      // The polling hook will detect MQTT is working by seeing recent updates
      if (chatId && state.pollingChats[chatId]) {
        console.log(`🔄 [SIMPLE_PROGRESS] MQTT update received for chatId: ${chatId}`);
      }
    },

    // ✅ Complete progress (remove from active list)
    completeProgress: (state, action: PayloadAction<{ queryId: string }>) => {
      const { queryId } = action.payload;
      delete state.activeProgresses[queryId];
      console.log(`✅ [SIMPLE_PROGRESS] Completed and removed queryId: ${queryId}`);
    },

    // ✅ Clear specific progress
    clearProgress: (state, action: PayloadAction<{ queryId: string }>) => {
      const { queryId } = action.payload;
      delete state.activeProgresses[queryId];
      console.log(`🗑️ [SIMPLE_PROGRESS] Cleared queryId: ${queryId}`);
    },

    // ✅ Clear all progresses
    clearAllProgress: (state) => {
      state.activeProgresses = {};
      console.log(`🗑️ [SIMPLE_PROGRESS] Cleared all progresses`);
    },

    // ✅ Clean up stale progresses (no updates for > 5 minutes)
    cleanupStaleProgresses: (state) => {
      const now = Date.now();
      const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
      
      const staleQueryIds: string[] = [];
      
      Object.entries(state.activeProgresses).forEach(([queryId, progress]) => {
        const timeSinceUpdate = now - progress.lastUpdate;
        
        if (timeSinceUpdate > STALE_THRESHOLD) {
          staleQueryIds.push(queryId);
        }
      });
      
      // Remove stale progresses
      staleQueryIds.forEach(queryId => {
        delete state.activeProgresses[queryId];
      });
      
      if (staleQueryIds.length > 0) {
        console.log(`🧹 [SIMPLE_PROGRESS] Removed ${staleQueryIds.length} stale progresses`);
      }
    },

    // ✅ Polling management actions
    startPolling: (state, action: PayloadAction<{ chatId: string; messageId: string; intervalId: number }>) => {
      const { chatId, messageId, intervalId } = action.payload;
      state.pollingChats[chatId] = {
        intervalId,
        messageId,
        lastChecked: Date.now()
      };
      console.log(`🔄 [SIMPLE_PROGRESS] Started polling for chatId: ${chatId}, messageId: ${messageId}`);
    },

    stopPolling: (state, action: PayloadAction<{ chatId: string }>) => {
      const { chatId } = action.payload;
      if (state.pollingChats[chatId]) {
        // Don't call clearInterval here - the React hook manages the actual interval
        // Just remove from Redux state tracking
        delete state.pollingChats[chatId];
        console.log(`⏹️ [SIMPLE_PROGRESS] Stopped polling for chatId: ${chatId}`);
      }
    },

    updatePollingCheck: (state, action: PayloadAction<{ chatId: string }>) => {
      const { chatId } = action.payload;
      if (state.pollingChats[chatId]) {
        state.pollingChats[chatId].lastChecked = Date.now();
      }
    },
  },
});

export const {
  startProgress,
  updateProgress,
  completeProgress,
  clearProgress,
  clearAllProgress,
  cleanupStaleProgresses,
  startPolling,
  stopPolling,
  updatePollingCheck,
} = simpleLegalProgressSlice.actions;

// ✅ Selectors for chat-specific progress
export const selectActiveProgressForChat = (state: { simpleLegalProgress: SimpleLegalProgressState }, chatId: string) => {
  const allProgresses = Object.values(state.simpleLegalProgress.activeProgresses);
  const activeProgress = allProgresses.find(
    progress => progress.chatId === chatId && progress.status === 'active'
  );
  
  // console.log(`🔍 [SIMPLE_PROGRESS_SELECTOR] Looking for active progress in chatId: ${chatId}`);
  // console.log(`🔍 [SIMPLE_PROGRESS_SELECTOR] All progresses:`, allProgresses.map(p => ({ queryId: p.queryId, chatId: p.chatId, status: p.status })));
  // console.log(`🔍 [SIMPLE_PROGRESS_SELECTOR] Found active progress:`, activeProgress ? { queryId: activeProgress.queryId, progress: activeProgress.overallProgress } : 'None');
  
  return activeProgress;
};

export const selectHasActiveProgressForChat = (state: { simpleLegalProgress: SimpleLegalProgressState }, chatId: string) => {
  const activeProgress = selectActiveProgressForChat(state, chatId);
  
  if (!activeProgress) return false;
  
  // ✅ For polling mode, don't check recency - if there's active progress, show it
  // The polling will handle timeout detection
  return true;
};

export default simpleLegalProgressSlice.reducer;